import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { lazy, Suspense, useEffect, useState } from 'react';
import { clearAuthToken, syncAuthTokenFromCookie } from './lib/authToken';
import { CartProvider } from './context/CartContext';
import { removeBootLoader, waitForPageReady } from './lib/pageReady';
import { site } from './config/site';
import Loader from './components/Loader';
import SmoothScroll from './components/SmoothScroll';
import SmartBazaarSale from './components/SmartBazaarSale';
import WhatsAppFloat from './components/WhatsAppFloat';
import './App.css';

const HomePage = lazy(() => import('./pages/HomePage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));

function RedirectExternal({ href }) {
  useEffect(() => {
    window.location.replace(href);
  }, [href]);

  return null;
}

function AuthBootstrap({ children }) {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const wantsLogout = params.get('ammachekku_react_logout') === '1';

    if (wantsLogout) {
      clearAuthToken();
      const redirectTo = params.get('redirect_to');
      params.delete('ammachekku_react_logout');
      params.delete('redirect_to');
      const remaining = params.toString();
      const cleanPath = `${location.pathname}${remaining ? `?${remaining}` : ''}`;

      if (redirectTo) {
        try {
          const target = new URL(redirectTo, window.location.origin);
          const isAmmaHost = /(^|\.)ammachekku\.com$/i.test(target.hostname);
          if (isAmmaHost || target.origin === window.location.origin) {
            window.location.replace(target.toString());
            return;
          }
        } catch {
          // fall through to clean local path
        }
      }

      navigate(cleanPath || '/', { replace: true });
      return;
    }

    syncAuthTokenFromCookie();
  }, [location.pathname, location.search, navigate]);

  return children;
}

const LIGHT_ROUTES = new Set([
  '/log-in',
  '/login',
  '/registration',
  '/register',
]);

/**
 * Shows the branded loader on refresh / navigation until the route
 * has mounted and visible images have finished rendering.
 */
function PageReadyGate({ children }) {
  const location = useLocation();
  const isLightRoute = LIGHT_ROUTES.has(location.pathname);
  const [showLoader, setShowLoader] = useState(!isLightRoute);

  useEffect(() => {
    let cancelled = false;

    if (isLightRoute) {
      setShowLoader(false);
      removeBootLoader();
      return undefined;
    }

    setShowLoader(true);

    (async () => {
      try {
        await waitForPageReady({
          minMs: 450,
          maxMs: 12000,
        });
      } catch {
        /* ignore — still hide loader */
      }

      if (!cancelled) {
        setShowLoader(false);
        removeBootLoader();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, isLightRoute]);

  return (
    <Suspense
      fallback={
        isLightRoute ? null : <Loader size={200} />
      }
    >
      {showLoader ? <Loader size={200} /> : null}
      {children}
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthBootstrap>
        <CartProvider>
          <PageReadyGate>
            <SmoothScroll>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/log-in" element={<LoginPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/registration" element={<RegisterPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/terms-and-conditions" element={<RedirectExternal href={site.policies.terms} />} />
                <Route path="/privacy-policy" element={<RedirectExternal href={site.policies.privacy} />} />
                <Route path="/return-and-refund-policy" element={<RedirectExternal href={site.policies.refund} />} />
                <Route path="/shipping-policy" element={<RedirectExternal href={site.policies.shipping} />} />
              </Routes>
            </SmoothScroll>
            {/* Outside SmoothScroll — GSAP transforms break position:fixed */}
            <SmartBazaarSale />
            <WhatsAppFloat />
          </PageReadyGate>
        </CartProvider>
      </AuthBootstrap>
    </BrowserRouter>
  );
}

export default App;
